// @ts-nocheck
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase, edgeFnUrl } from '@saas-boilerplate/shared';
import { supabaseEdgeFetchHeaders } from '@/lib/edge-functions';
import { useWorkspace } from '@/contexts/context';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/components/ui/utils';
import {
  ArrowRight,
  BadgeCheck,
  History,
  Info,
  Layers,
  Loader2,
  Mail,
  PlusCircle,
  Shield,
  Leaf,
} from 'lucide-react';
import {
  WorkspaceFlowShell,
  WorkspaceFlowMainBackdrop,
  WorkspaceFlowSegmentedStepper,
} from '@/components/workspace-flow-shell';
import { WORKSPACE_RETENTION_PRESETS } from '@/lib/workspace-retention';

/** Create workspace is step 1 (workspace-select); wizard order: Policies → Invites (steps 2–3 of 3). */
const ONBOARDING_TOTAL_STEPS = 3;

const STEPS = [
  {
    id: 'policies',
    title: 'Set Policies',
    description:
      'Define the boundaries of your workspace. These settings ensure transparency and trust across all workspace interactions.',
  },
  {
    id: 'team',
    title: 'Assemble your core team',
    description:
      'Invite the voices that matter most. Collaboration in your workspace is built on quiet focus and intentional sharing.',
  },
] as const;

type StepId = (typeof STEPS)[number]['id'];

const BENTO_IMAGE =
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80&auto=format&fit=crop';

const RETENTION_PRESETS = WORKSPACE_RETENTION_PRESETS;

export function WorkspaceOnboardingWizard({
  onComplete,
  onSignOut,
  onSwitchWorkspace,
}: {
  onComplete: () => void;
  onSignOut: () => void;
  /** Return to workspace selection (e.g. from the first onboarding step). */
  onSwitchWorkspace: () => void;
}) {
  const { workspace, refreshWorkspace } = useWorkspace();
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);

  const [retentionDays, setRetentionDays] = useState('90');
  const [consentCopy, setConsentCopy] = useState('');

  /** Email rows for team invites (names derived from local part when sending). */
  const [inviteEmails, setInviteEmails] = useState<string[]>(['', '', '']);

  const step = STEPS[stepIndex].id as StepId;

  useEffect(() => {
    if (!workspace?.settings) return;
    const s = workspace.settings;
    if (typeof s.retention_days === 'number' && [30, 90, 180, 360].includes(s.retention_days)) {
      setRetentionDays(String(s.retention_days));
    } else if (s.retention_days != null) {
      setRetentionDays('90');
    }
    if (typeof s.default_consent_copy === 'string') setConsentCopy(s.default_consent_copy);
  }, [workspace?.settings]);

  const persistSetupFlag = async (extraSettings: Record<string, unknown> = {}) => {
    if (!workspace) return;
    const prev = (workspace.settings && typeof workspace.settings === 'object' ? workspace.settings : {}) as Record<
      string,
      unknown
    >;
    const settings = {
      ...prev,
      ...extraSettings,
      workspace_setup_completed_at: new Date().toISOString(),
    };
    const { error } = await supabase.from('workspaces').update({ settings }).eq('id', workspace.id);
    if (error) throw error;
    await refreshWorkspace({ silent: true });
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await persistSetupFlag();
      toast.message('Setup skipped - you can finish later in Settings.');
      onComplete();
    } catch (e: any) {
      toast.error(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const handlePoliciesNext = async () => {
    if (!workspace) return;
    setSaving(true);
    try {
      const prev = (workspace.settings && typeof workspace.settings === 'object' ? workspace.settings : {}) as Record<
        string,
        unknown
      >;
      const rd = retentionDays.trim() ? parseInt(retentionDays, 10) : null;
      const settings = {
        ...prev,
        ...(rd != null && !Number.isNaN(rd) ? { retention_days: rd } : {}),
        ...(consentCopy.trim() ? { default_consent_copy: consentCopy.trim() } : {}),
      };
      const { error } = await supabase.from('workspaces').update({ settings }).eq('id', workspace.id);
      if (error) throw error;
      await refreshWorkspace({ silent: true });
      setStepIndex((i) => i + 1);
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const deriveInviteeName = (email: string) => {
    const local = email.split('@')[0]?.trim() || 'teammate';
    return local
      .replace(/[._-]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ') || 'Teammate';
  };

  const sendInvitationToEmail = async (email: string) => {
    if (!workspace) throw new Error('No workspace');
    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();
    const { data: currentProfile } = await supabase.from('profiles').select('full_name').eq('id', currentUser?.id).single();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Sign in required');
    const res = await fetch(`${edgeFnUrl('invitation')}/send-invitation`, {
      method: 'POST',
      headers: supabaseEdgeFetchHeaders(session.access_token),
      body: JSON.stringify({
        inviteeEmail: email.trim(),
        inviteeName: deriveInviteeName(email),
        workspaceId: workspace.id,
        workspaceName: workspace.name,
        inviterName: currentProfile?.full_name || currentUser?.email,
        role: 'member',
      }),
    });
    const ct = res.headers.get('content-type');
    let result: any;
    if (ct?.includes('application/json')) {
      result = await res.json();
    }
    if (!res.ok) {
      throw new Error(result?.error || result?.message || 'Invitation failed');
    }
  };

  const handleFinishSetup = async () => {
    if (!workspace) return;
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const raw of inviteEmails) {
      const t = raw.trim();
      if (!t) continue;
      if (!emailRe.test(t)) {
        toast.error(`Fix invalid email: ${t}`);
        return;
      }
    }
    setSaving(true);
    try {
      const seen = new Set<string>();
      let sent = 0;
      for (const raw of inviteEmails) {
        const email = raw.trim().toLowerCase();
        if (!email || seen.has(email)) continue;
        seen.add(email);
        await sendInvitationToEmail(email);
        sent++;
      }
      if (sent > 0) {
        toast.success(sent === 1 ? 'Invitation sent' : `${sent} invitations sent`);
      }
      await persistSetupFlag();
      onComplete();
    } catch (e: any) {
      toast.error(e?.message || 'Could not finish setup');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteThemLater = async () => {
    setSaving(true);
    try {
      await persistSetupFlag();
      onComplete();
    } catch (e: any) {
      toast.error(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceFlowShell onSignOut={onSignOut}>
      <main className="relative flex flex-col items-center px-6 py-12 md:py-16">
        <WorkspaceFlowMainBackdrop />

        <section className={cn('w-full', step === 'policies' || step === 'team' ? 'max-w-6xl' : 'max-w-2xl')}>
          <div className="mb-8 flex justify-center px-2 md:mb-10">
            <WorkspaceFlowSegmentedStepper
              current={stepIndex + 2}
              total={ONBOARDING_TOTAL_STEPS}
              className="max-w-[280px]"
            />
          </div>
          <div
            className={cn(
              'mb-10 text-center md:mb-12',
              (step === 'policies' || step === 'team') && 'mb-16',
            )}
          >
            {step === 'team' ? (
              <>
                <h1 className="font-display text-4xl font-light tracking-tight text-foreground md:text-5xl">
                  Assemble your{' '}
                  <span className="font-semibold italic text-primary">core team</span>
                </h1>
                <p className="mx-auto mt-4 max-w-lg text-lg font-light leading-relaxed text-muted-foreground">
                  {STEPS[stepIndex].description}
                </p>
              </>
            ) : (
              <>
                <h1 className="font-display text-4xl font-light tracking-tight text-foreground md:text-5xl">
                  {STEPS[stepIndex].title}
                </h1>
                <p
                  className={cn(
                    'mx-auto mt-4 text-lg font-light leading-relaxed text-muted-foreground',
                    step === 'policies' ? 'max-w-lg' : 'max-w-md',
                  )}
                >
                  {STEPS[stepIndex].description}
                </p>
              </>
            )}
          </div>

          {step === 'policies' ? (
            <>
              <div className="grid w-full grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
                <div className="space-y-8 lg:col-span-7">
                  <section className="rounded-lg bg-surface-container-low p-8 md:p-10">
                    <div className="mb-6 flex items-center gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#ffdfa0]">
                        <Shield className="h-6 w-6 text-[#261a00]" strokeWidth={1.75} />
                      </div>
                      <div>
                        <h2 className="font-display text-xl font-medium tracking-wide text-foreground">
                          Recording Consent Policy
                        </h2>
                        <p className="text-sm font-light text-muted-foreground">
                          Custom message shown to meeting participants.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <Label
                        htmlFor="onb-consent"
                        className="block font-sans text-xs font-semibold uppercase tracking-widest text-muted-foreground"
                      >
                        Policy message
                      </Label>
                      <Textarea
                        id="onb-consent"
                        rows={6}
                        value={consentCopy}
                        onChange={(e) => setConsentCopy(e.target.value)}
                        placeholder="e.g., Upstand.ai will record this session for internal training and quality assurance purposes. By staying, you consent to being recorded."
                        className="w-full resize-y rounded-sm border-0 bg-white px-4 py-4 text-sm text-foreground shadow-none ring-0 placeholder:text-outline-variant/60 focus-visible:ring-2 focus-visible:ring-primary/20"
                      />
                      <div className="flex items-center gap-2 px-1 text-xs italic text-on-tertiary-container/80">
                        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span>This message will be audible or visible upon entry.</span>
                      </div>
                    </div>
                  </section>

                  <div className="relative flex h-64 items-center justify-center overflow-hidden rounded-lg bg-[#eae8e4]/40">
                    <div className="absolute inset-0 opacity-10">
                      <img
                        src={BENTO_IMAGE}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                      />
                    </div>
                    <div className="relative z-10 flex flex-col items-center">
                      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full onboarding-primary-gradient shadow-2xl shadow-primary/20">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/60 backdrop-blur-md">
                          <Leaf className="h-6 w-6 text-primary" strokeWidth={1.5} />
                        </div>
                      </div>
                      <span className="font-display text-xs font-light tracking-[0.2em] text-muted-foreground">
                        YOUR WORKSPACE CORE
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-8 lg:col-span-5">
                  <section className="rounded-lg bg-surface-container-low p-8 md:p-10">
                    <div className="mb-8 flex items-center gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#ffdcc3]">
                        <History className="h-6 w-6 text-[#2f1500]" strokeWidth={1.75} />
                      </div>
                      <div>
                        <h2 className="font-display text-xl font-medium tracking-wide text-foreground">Data Retention</h2>
                        <p className="text-sm font-light text-muted-foreground">How long should we store records?</p>
                      </div>
                    </div>
                    <div className="space-y-4" role="radiogroup" aria-label="Data retention period">
                      {RETENTION_PRESETS.map((opt) => {
                        const selected = retentionDays === String(opt.days);
                        return (
                          <button
                            key={opt.days}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => setRetentionDays(String(opt.days))}
                            className={cn(
                              'group relative flex w-full items-center rounded-md p-6 text-left transition-all',
                              selected
                                ? 'cursor-pointer bg-white shadow-xl shadow-primary/5 ring-1 ring-primary/30'
                                : 'cursor-pointer border border-transparent bg-white hover:border-outline-variant/10 hover:bg-surface-container-high',
                            )}
                          >
                            <div className="flex-1">
                              <h3
                                className={cn(
                                  'font-display text-lg',
                                  selected ? 'font-bold text-primary' : 'font-medium text-foreground',
                                )}
                              >
                                {opt.title}
                              </h3>
                              <p className="text-xs font-light text-muted-foreground">{opt.subtitle}</p>
                            </div>
                            <div
                              className={cn(
                                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
                                selected ? 'border-primary' : 'border-outline-variant group-hover:border-primary',
                              )}
                            >
                              <div
                                className={cn(
                                  'h-3 w-3 rounded-full bg-primary transition-opacity',
                                  selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                                )}
                              />
                            </div>
                            {opt.recommended ? (
                              <span className="absolute -right-2 -top-2 rounded-full bg-primary px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-foreground">
                                Recommended
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-8 rounded-lg border border-outline-variant/10 bg-white/40 p-6">
                      <div className="flex gap-4">
                        <Shield className="h-5 w-5 shrink-0 text-tertiary" aria-hidden />
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          All data is encrypted at rest and in transit. After the retention period, records are permanently
                          purged from our infrastructure.
                        </p>
                      </div>
                    </div>
                  </section>

                  <div className="flex flex-col gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onSwitchWorkspace}
                      disabled={saving}
                      className="w-full sm:w-auto"
                    >
                      Back
                    </Button>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={handlePoliciesNext}
                      className="w-full rounded-md py-5 font-display text-sm font-medium uppercase tracking-widest text-white shadow-2xl shadow-primary/20 transition-opacity onboarding-primary-gradient hover:opacity-95 active:scale-[0.98] disabled:opacity-60"
                    >
                      {saving ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Continue to invites'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-16 text-center">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={saving}
                  className="text-muted-foreground"
                >
                  Skip setup
                </Button>
              </div>
            </>
          ) : step === 'team' ? (
            <div className="flex w-full flex-col items-center">
              <div className="mx-auto grid w-full max-w-4xl grid-cols-1 items-start gap-8 md:grid-cols-12">
                <div className="space-y-6 md:col-span-7">
                  <div className="relative overflow-hidden rounded-lg bg-white p-8 shadow-[0_20px_50px_rgba(0,0,0,0.02)]">
                    <div className="mb-8 flex items-center gap-3">
                      <Mail className="h-5 w-5 text-primary" strokeWidth={1.75} aria-hidden />
                      <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                        Email invitations
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {inviteEmails.map((value, i) => (
                        <div key={i} className="relative">
                          <input
                            type="email"
                            value={value}
                            onChange={(e) => {
                              const next = [...inviteEmails];
                              next[i] = e.target.value;
                              setInviteEmails(next);
                            }}
                            placeholder={
                              i === 0
                                ? 'teammate@company.com'
                                : i === 1
                                  ? 'designer@studio.com'
                                  : 'Enter another email...'
                            }
                            className="w-full rounded-sm border-0 border-b-2 border-outline-variant/30 bg-surface-container-low px-4 py-3 text-sm shadow-none ring-0 transition-all placeholder:text-stone-400 focus:border-primary focus:ring-0 focus-visible:outline-none"
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      disabled={inviteEmails.length >= 20}
                      onClick={() =>
                        setInviteEmails((prev) => (prev.length >= 20 ? prev : [...prev, '']))
                      }
                      className="mt-8 flex items-center gap-2 text-sm font-medium text-primary transition-opacity hover:opacity-80 disabled:opacity-40"
                    >
                      <PlusCircle className="h-4 w-4" aria-hidden />
                      Add more invitees
                    </button>
                  </div>
                </div>

                <div className="space-y-6 md:col-span-5">
                  <div className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border-2 border-dashed border-outline-variant/35 bg-surface-container-low/40 p-8 text-center">
                    <Layers className="h-10 w-10 text-muted-foreground/40" strokeWidth={1.25} aria-hidden />
                    <p className="mt-4 font-display text-sm font-medium text-muted-foreground">More integrations</p>
                    <p className="mt-2 max-w-[220px] text-xs leading-relaxed text-muted-foreground/80">
                      Placeholder - team tools and imports will show here in a future release.
                    </p>
                  </div>
                  <div className="rounded-lg bg-surface-container-low p-6">
                    <div className="flex gap-4">
                      <Info className="h-5 w-5 shrink-0 text-primary-container" aria-hidden />
                      <p className="text-[11px] leading-snug text-muted-foreground">
                        You can also share a <span className="font-medium text-primary">magic link</span> with your team
                        later if you&apos;re not ready yet.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-20 flex w-full max-w-md flex-col items-center gap-8">
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleFinishSetup}
                  className="group relative w-full overflow-hidden rounded-md bg-gradient-to-br from-primary to-primary-container px-8 py-5 text-lg font-semibold text-white shadow-xl shadow-primary/20 transition-all hover:scale-[1.02] hover:shadow-primary/40 active:scale-95 disabled:opacity-60"
                >
                  <div className="absolute inset-0 bg-white/10 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                  <span className="relative z-10 flex items-center justify-center gap-3 font-display">
                    {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Finish setup'}
                    {!saving ? <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden /> : null}
                  </span>
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleInviteThemLater}
                  className="flex items-center gap-2 font-display text-sm tracking-widest text-muted-foreground transition-colors hover:text-primary"
                >
                  I&apos;ll invite them later
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </button>
              </div>

              <div className="mt-12 flex w-full max-w-md flex-wrap items-center justify-center gap-4 border-t border-border/30 pt-8">
                <Button type="button" variant="outline" onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={saving}>
                  Back
                </Button>
                <Button type="button" variant="ghost" onClick={handleSkip} disabled={saving} className="text-muted-foreground">
                  Skip setup
                </Button>
              </div>

              <div className="mt-24 w-full border-t border-outline-variant/10 pt-12 text-center">
                <div className="flex flex-wrap items-center justify-center gap-8 opacity-50 grayscale transition-all duration-700 hover:grayscale-0">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" aria-hidden />
                    <span className="text-xs uppercase tracking-tighter text-muted-foreground">Enterprise encryption</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BadgeCheck className="h-5 w-5" aria-hidden />
                    <span className="text-xs uppercase tracking-tighter text-muted-foreground">SOC2 compliant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Info className="h-5 w-5" aria-hidden />
                    <span className="text-xs uppercase tracking-tighter text-muted-foreground">GDPR ready</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </section>

      </main>
    </WorkspaceFlowShell>
  );
}
